import { Box, Button, Chip, Typography, Autocomplete, TextField, Alert } from '@mui/material';
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from '@mui/icons-material/Download';
import { AuthContext } from "context";
import { createMRTColumnHelper } from 'material-react-table';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import MDAlert from "../../components/MDAlert";
import MDBox from "../../components/MDBox";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";
import GenericTable from "../genericTable";

// Assuming GenericTable and other necessary components are imported

function ManagementSecuritySupplier() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [isSecurityManager, setIsSecurityManager] = useState(false);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const companyColumnHelper = createMRTColumnHelper();

  const handleSetNotification = (msg, severityType) => {
    if (severityType === 'success') {
      toast.success(msg, {id: 'managment-security-supplier-toast'});
    } else {
      toast.error(msg, {id: 'managment-security-supplier-toast'});
    }
  };

  function fetchAuthorizedSuppliers() {
    setLoadingSuppliers(true);

    apiService.get("/suppliers-management", { get_authorized_suppliers: true }, {})
      .then((response) => {
        if (response) {
          setAvailableSuppliers(response);
          setLoadingSuppliers(false);
        }
      })
      .catch(error => {
        handleSetNotification(
          "Errore durante il caricamento dei fornitori",
          "error"
        );
        setLoadingSuppliers(false);
      });
  }

  function fetchSupplierDocument(supplierId = null){
      setLoadingSupplier(true);

      const supplierIdToUse = supplierId || localStorage.getItem("supplier");

      apiService.get("/get-documents", {supplier_id: supplierIdToUse},{})
        .then((response) => {
          if (response) {
            setCompanyDocuments(response);
            setLoadingSupplier(false);
          }
        })
        .catch(error => {
          handleSetNotification(
              "Errore durante la chiamata al server, codice d'errore #08514262429906",
              "error"
          );
          setLoadingSupplier(false);
      })
  }


  useEffect(() => {
    // Verifica se è un security manager (utente interno) o un supplier worker
    const supplierIdInLocalStorage = localStorage.getItem("supplier");

    if (authContext?.hasAuth("UPSERT_DOCUMENTS_SUPPLIER") && !supplierIdInLocalStorage) {
      // È un security manager interno
      setIsSecurityManager(true);
      fetchAuthorizedSuppliers();
    } else {
      // È un supplier worker, carica i documenti del proprio fornitore
      setIsSecurityManager(false);
      fetchSupplierDocument();
    }
  }, []);

  const companyColumns = [
    companyColumnHelper.accessor('label', {
      header: 'Nome',
      size: 150,
    }),
    companyColumnHelper.accessor('status', {
      header: 'Stato',
      size: 120,
      Cell: ({ cell }) => (
        <Chip
          label={getStatusName(cell.getValue())}
          color={getStatusColor(cell.getValue(), cell.row.original.required)}
          size="medium"
          sx={{ fontWeight: 'medium' }}
        />
      ),
    }),
    companyColumnHelper.accessor('required', {
      header: 'Richiesto',
      size: 100,
      Cell: ({ cell }) => (
        <Typography variant="body2">
          {cell.getValue()  ? 'Necessario' : 'Opzionale'}
        </Typography>
      ),
    }),
    companyColumnHelper.accessor('additionalInformation', {
      header: 'Informazioni Aggiuntive',
      size: 250,
      Cell: ({ cell }) => (
        <Box>
          {cell.getValue()?.map((info, index) => (
            <Typography key={index} variant="body2">
              {info.label}: {info.value || 'N/A'}
            </Typography>
          ))}
        </Box>
      ),
    }),
    companyColumnHelper.accessor('url', {
      header: 'Azioni',
      size: 100,
      Cell: ({ cell }) => (
        cell.row.original.status !== 'toUpload' &&
        <Button
          variant="outlined"
          size="small"
          onClick={() => window.open(cell.getValue(), '_blank')}
          sx={{
                marginTop: "10px", marginBottom: "30px", backgroundColor: '#514f4f',
                color: 'white!important',
                mb: 2,
                width: '80px',
                height: '25px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                justifyContent: 'center',
                borderColor: 'black',
                gap: '8px',
                '&:hover': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                },
                '&:focus': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                },
                '&:active': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                }
              }}
        >
          Visualizza
        </Button>
      ),
    }),
  ];

  const handleSupplierChange = (event, newValue) => {
    setSelectedSupplier(newValue);
    if (newValue) {
      // Salva in sessionStorage per mantenerlo durante la navigazione
      sessionStorage.setItem('selectedSupplier', JSON.stringify(newValue));
      fetchSupplierDocument(newValue.id);
    } else {
      sessionStorage.removeItem('selectedSupplier');
      setCompanyDocuments([]);
    }
  };

  const handleNavigateToUpload = () => {
    if (isSecurityManager && selectedSupplier) {
      navigate(`/security-document-upload/supplier/?supplier_id=${selectedSupplier.id}`);
    } else {
      navigate(`/security-document-upload/supplier/`);
    }
  };

  const handleDownloadZip = async () => {
    const supplierId = isSecurityManager ? selectedSupplier?.id : localStorage.getItem("supplier");
    if (!supplierId) {
      toast.error('Seleziona prima un fornitore', { id: 'download-zip-toast' });
      return;
    }

    setDownloadingZip(true);
    try {
      const params = new URLSearchParams({
        download_type: 'supplier_company',
        supplier_id: supplierId
      });

      const token = localStorage.getItem("token");
      const baseUrl = window.location.hostname.split(".")[0] !== "localhost"
        ? `https://${window.location.hostname.split(".")[0]}.helpmate.it/api`
        : process.env.REACT_APP_API_URL_BACKEND;

      const response = await fetch(
        `${baseUrl}/download-security-documents-zip/?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Download failed');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'documenti_aziendali.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download completato', { id: 'download-zip-toast' });
    } catch (error) {
      toast.error(error.message || 'Errore durante il download', { id: 'download-zip-toast' });
      console.error('Download error:', error);
    }
    setDownloadingZip(false);
  };

  return (
    <>
    <DashboardLayout>
      { authContext.hasAuth("VIEW_DOCUMENTS_SUPPLIER") ?
        <>
      <DashboardNavbar></DashboardNavbar>
    <Box sx={{ p: 3 }}>
      <Box sx={{mb: 4}}>
        <center>
          <Typography variant="h5" gutterBottom>
            DOCUMENTI AZIENDALI
          </Typography>

          {/* Dropdown per selezione fornitore - solo per security manager */}
          {isSecurityManager && (
            <Box sx={{ maxWidth: 500, margin: '20px auto' }}>
              {loadingSuppliers ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <Autocomplete
                    value={selectedSupplier}
                    onChange={handleSupplierChange}
                    options={availableSuppliers}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleziona Fornitore"
                        placeholder="Cerca fornitore..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.areas?.length > 0
                              ? `Aree: ${option.areas.map(a => a.name).join(', ')}`
                              : 'Nessuna area assegnata'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText="Nessun fornitore disponibile"
                  />
                  {selectedSupplier && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      I documenti caricati per questo fornitore richiederanno approvazione
                    </Alert>
                  )}
                </>
              )}
            </Box>
          )}

          { (authContext?.hasAuth("UPSERT_DOCUMENTS_SUPPLIER") && !loadingSupplier && (!isSecurityManager || selectedSupplier)) &&
            <Button
              variant="contained"
              color="primary"
              onClick={handleNavigateToUpload}
              sx={{
                marginTop: "10px", marginBottom: "30px", backgroundColor: '#514f4f',
                color: 'white!important',
                mb: 2,
                width: '220px',
                height: '50px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                justifyContent: 'center',
                gap: '8px',
                '&:hover': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                },
                '&:focus': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                },
                '&:active': {
                  backgroundColor: '#514f4f',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                }
              }}
            >
              Gestisci Documenti
            </Button>
          }
          { !loadingSupplier && companyDocuments.length > 0 && (!isSecurityManager || selectedSupplier) &&
            <Button
              variant="contained"
              onClick={handleDownloadZip}
              disabled={downloadingZip}
              startIcon={downloadingZip ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              sx={{
                marginTop: "10px", marginBottom: "30px", backgroundColor: '#514f4f',
                color: 'white!important',
                mb: 2,
                width: '280px',
                height: '50px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                justifyContent: 'center',
                gap: '8px',
                '&:hover': {
                  backgroundColor: '#3d3b3b',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)',
                  color: 'white!important',
                },
                '&:disabled': {
                  backgroundColor: '#7a7777',
                  color: 'rgba(255,255,255,0.7)!important',
                }
              }}
            >
              {downloadingZip ? 'Download in corso...' : 'Scarica Documenti Aziendali'}
            </Button>
          }
          {loadingSupplier &&  <CircularProgress size={120} sx={{marginTop: "250px", color: "#2b84ea"}}/> }
        </center>
        { !loadingSupplier && companyDocuments.length > 0 && <GenericTable
          data={companyDocuments}
          columns={companyColumns}
        />}
        { !loadingSupplier && companyDocuments.length === 0 && selectedSupplier && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nessun documento trovato per questo fornitore
          </Alert>
        )}
      </Box>
    </Box>
      </>
      :
         <MDBox mt={8}>
                  <MDAlert color="error">Non hai i permessi per visualizzare questa pagina</MDAlert>
         </MDBox>
      }
    </DashboardLayout>
    </>
  );
}

// Helper functions

function getStatusColor(status, required) {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejecteddone':
      return 'info';
    case 'toapprove':
      return 'warning';
    case 'toupload':
      if (required) {
        return 'error';
      }
      return 'secondary';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

function getStatusName(status) {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'Approvato';
    case 'rejecteddone':
      return 'Rettificato';
    case 'toapprove':
      return 'In attesa';
    case 'toupload':
      return "Da caricare"
    case 'rejected':
      return 'Da rettificare';
    default:
      return 'default';
  }
}

export default ManagementSecuritySupplier;
